import React from 'react';

import { capitalize } from '@mui/material';
import { t as translate } from 'i18next';

import CollaboratorsToAddTableActions from '../../pages/Collaborators/sections/CollaboratorsToAddTable/CollaboratorsToAddTableActions/CollaboratorsToAddTableActions';
import BdsLabelPill from '../components/v3/BdsLabelPill/BdsLabelPill';
import BdsTooltip from '../components/v3/BdsTooltip/BdsTooltip';
import ChallengesTableActions from '../components/v3/Table/StyledColumns/ChallengesTableActions/ChallengesTableActions';
import CollaboratorsActions from '../components/v3/Table/StyledColumns/CollaboratorsColumns/CollaboratorsActions/CollaboratorsActions';
import CollaboratorsDocValue from '../components/v3/Table/StyledColumns/CollaboratorsColumns/CollaboratorsDocValue/CollaboratorsDocValue';
import CollaboratorsPlan from '../components/v3/Table/StyledColumns/CollaboratorsColumns/CollaboratorsPlan/CollaboratorsPlan';
import CollaboratorStatusCell from '../components/v3/Table/StyledColumns/CollaboratorsColumns/CollaboratorStatusCell/CollaboratorStatusCell';
import FormattedDate from '../components/v3/Table/StyledColumns/Dates/FormattedDate/FormattedDate';
import FormattedDateRange from '../components/v3/Table/StyledColumns/Dates/FormattedDateRange/FormattedDateRange';
import HealthReportsTableActions from '../components/v3/Table/StyledColumns/Reports/HealthReportsTableActions/HealthReportsTableActions';
import MonthlyReportsTableActions from '../components/v3/Table/StyledColumns/Reports/MonthlyReportsTableActions/MonthlyReportsTableActions';
import { PayrollReportsTableActions } from '../components/v3/Table/StyledColumns/Reports/PayrollReportsTableActions/PayrollReportsTableActions';
import TruncatedText from '../components/v3/Table/StyledColumns/TruncatedText/TruncatedText';
import StyledTableEditableRowItem from '../components/v3/Table/StyledTableEditableRow/StyledTableEditableRowItem';
import { BENEFIT_PROVIDER_STATUS_DATA } from '../constants/benefits';
import { CHALLENGES_STATUS } from '../constants/challenges';
import { REPORTS_STATUS } from '../constants/reports';
import { FLEXIBLE_REMUNERATION_REQUEST_STATUS } from '../constants/requests';
import { IBdsLabelPillImage } from '../interfaces/bdsLabelPill';
import { IMembersData } from '../interfaces/businessData';
import { IChallengeDetail } from '../interfaces/challenges';
import {
  ICollaboratorDependantBenefit,
  IToAddMembersData,
  IUnsubscribedMembersData
} from '../interfaces/collaborators';
import { IFlexibleRemunerationRequest } from '../interfaces/company';
import { IDependentsWithErrors, IMemberByFileData } from '../interfaces/manageCollaborators';
import { IHealthReportList, IReport } from '../interfaces/reports';
import { TRANSLATIONS_CHALLENGES_MAIN } from '../translations/challenges';
import { TRANSLATION_COLLABORATORS, TRANSLATIONS_COLLABORATOR_STATUS_V3 } from '../translations/collaborators';
import { TRANSLATION_MANAGE_COLLABORATORS } from '../translations/manageCollaborators';
import { TRANSLATIONS_REPORTS } from '../translations/reports';
import { TRANSLATIONS_REQUESTS } from '../translations/requests';
import { GenericFunction } from '../types/genericTypes';
import type { ReportTableType } from '../types/metrics';
import { BdsLabelPillVariant } from '../types/types';
import { formatDateToDDMMYYYY } from '../utils/datetime/datetime';
import { getLocalizedMonthName, invertDateFormat, objectHasKey, toLocaleNumberFormat } from '../utils/utils';

export const CHALLENGES_TABLE_COLUMNS = [
  {
    name: TRANSLATIONS_CHALLENGES_MAIN.table.header.title,
    key: 'name',
    class: '',
    isSortable: true,
    render: (challenge: IChallengeDetail) => {
      return (
        <div>
          <TruncatedText text={challenge.name} />
        </div>
      );
    }
  },
  {
    name: TRANSLATIONS_CHALLENGES_MAIN.table.header.status,
    key: 'status',
    isSortable: true,
    class: 'styled-table-hide-mobile challenges-table-column-status',
    render: (challenge: IChallengeDetail) => {
      const statusText = translate(TRANSLATIONS_CHALLENGES_MAIN.table.status[challenge.status]);
      return (
        <BdsLabelPill
          text={(statusText || '').toUpperCase()}
          variant={CHALLENGES_STATUS[challenge.status].tagVariant}
          iconLeft={CHALLENGES_STATUS[challenge.status].icon}
        />
      );
    }
  },
  {
    name: TRANSLATIONS_CHALLENGES_MAIN.table.header.type,
    key: 'type',
    isSortable: true,
    class: 'styled-table-hide-mobile',
    render: (challenge: IChallengeDetail) => {
      return <div>{translate(challenge.typeName ?? TRANSLATIONS_CHALLENGES_MAIN.table.defaultType)}</div>;
    }
  },
  {
    name: TRANSLATIONS_CHALLENGES_MAIN.table.header.participants,
    key: 'participants',
    isSortable: true,
    class: 'styled-table-hide-tablet'
  },
  {
    name: TRANSLATIONS_CHALLENGES_MAIN.table.header.period,
    key: 'period',
    isSortable: true,
    class: 'styled-table-hide-tablet',
    render: (challenge: IChallengeDetail) => {
      return <FormattedDateRange startDate={challenge.startAt} endDate={challenge.endAt} />;
    }
  },
  {
    name: TRANSLATIONS_CHALLENGES_MAIN.table.header.author,
    key: 'author',
    isSortable: true,
    class: 'styled-table-hide-tablet',
    render: (challenge: IChallengeDetail) => {
      return (
        <div>
          <TruncatedText text={challenge.author} />
        </div>
      );
    }
  },
  {
    name: '',
    key: 'actions',
    class: 'styled-table-hide-tablet',
    render: (
      challenge: IChallengeDetail,
      isActive: boolean,
      onRowClickMobile: GenericFunction,
      onChange: GenericFunction
    ) => {
      return (
        <ChallengesTableActions
          challenge={challenge}
          isActive={isActive}
          action={onRowClickMobile}
          onChange={onChange}
        />
      );
    }
  }
];

export const COLLABORATORS_TABLE_COLUMNS = [
  {
    name: TRANSLATION_COLLABORATORS.table.headers.name,
    key: 'fullName',
    isSortable: true,
    class: 'table-hide-tablet col-column',
    render: (collaborator: IMembersData) => {
      return collaborator.fullName ? (
        <div className="w-fit" style={{ minWidth: 220 }}>
          <TruncatedText text={collaborator.fullName} maxWidth="220px" />
        </div>
      ) : (
        <div className="placeholder">{translate(TRANSLATIONS_COLLABORATOR_STATUS_V3.pending)}</div>
      );
    }
  },
  {
    name: TRANSLATION_COLLABORATORS.table.headers.email,
    key: 'email',
    isSortable: true,
    class: 'col-column',
    render: (collaborator: IMembersData) => {
      return (
        <div className="w-fit" style={{ minWidth: 240 }}>
          <TruncatedText text={collaborator.email} maxWidth="240px" />
        </div>
      );
    }
  },
  {
    name: TRANSLATION_COLLABORATORS.table.headers.status,
    key: 'status',
    isSortable: true,
    class: 'table-hide-mobile table-align-right-tablet col-column',
    width: 135,
    render: (collaborator: IMembersData) => {
      return <CollaboratorStatusCell collaborator={collaborator} />;
    }
  },
  {
    name: TRANSLATION_COLLABORATORS.table.headers.onboardingDate,
    key: 'joinedAt',
    isSortable: true,
    class: 'table-hide-tablet col-column',
    render: (collaborator: IMembersData) => {
      return collaborator.joinedAt ? (
        <FormattedDate dateString={collaborator.joinedAt} separator="-" />
      ) : (
        <div className="placeholder" style={{ minWidth: 140 }}>
          {translate(TRANSLATIONS_COLLABORATOR_STATUS_V3.pending)}
        </div>
      );
    }
  },
  {
    name: TRANSLATION_COLLABORATORS.table.headers.plan,
    key: 'plan',
    class: 'table-hide-tablet table-align-right-tablet col-column',
    render: (collaborator: IMembersData) => {
      return (
        <div style={{ minWidth: 240 }}>
          <CollaboratorsPlan collaborator={collaborator} />
        </div>
      );
    }
  },
  {
    name: TRANSLATION_COLLABORATORS.table.headers.identification,
    key: 'identification',
    class: 'table-hide-mobile table-align-right-tablet col-column',
    render: (collaborator: IMembersData) => {
      return <CollaboratorsDocValue collaborator={collaborator} />;
    }
  },
  {
    name: '',
    key: 'actions',
    class: 'table-hide-mobile table-align-center',
    render: (member: IMembersData, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <CollaboratorsActions member={member} onChange={onChange} />;
    }
  }
];

export const COLLABORATORS_UNSUBSCRIPTIONS_TABLE_COLUMNS = [
  {
    name: TRANSLATION_COLLABORATORS.table.headers.email,
    key: 'email',
    isSortable: true,
    class: ''
  },
  {
    name: TRANSLATION_COLLABORATORS.table.headers.name,
    key: 'fullName',
    isSortable: true,
    class: ''
  },
  {
    name: TRANSLATION_COLLABORATORS.table.headers.eliminationDate,
    key: 'unsubscribedAt',
    isSortable: true,
    class: '',
    render: (member: IUnsubscribedMembersData) => {
      return <FormattedDate dateString={member.unsubscribedAt} />;
    }
  },
  {
    name: TRANSLATION_COLLABORATORS.table.headers.eliminationAuthor,
    key: 'requesterEmail',
    isSortable: true,
    class: ''
  }
];

export const COLLABORATORS_TO_ADD_TABLE_COLUMNS = [
  {
    name: TRANSLATION_COLLABORATORS.table.headers.email,
    key: 'email',
    isSortable: true,
    class: ''
  },
  {
    name: TRANSLATION_COLLABORATORS.table.headers.name,
    key: 'lastName',
    isSortable: true,
    class: '',
    render: (member: IToAddMembersData) => {
      return (
        <div>
          {capitalize(member.lastName)}, {capitalize(member.name)}
        </div>
      );
    }
  },
  {
    name: TRANSLATION_COLLABORATORS.table.headers.location,
    key: 'location',
    isSortable: true,
    class: '',
    render: (member: IToAddMembersData) => {
      return <div>{member.location}</div>;
    }
  },
  {
    name: TRANSLATION_COLLABORATORS.table.headers.hiringDate,
    key: 'startDate',
    isSortable: true,
    class: '',
    render: (member: IToAddMembersData) => {
      return <FormattedDate dateString={member.startDate} />;
    }
  },
  {
    name: '',
    key: 'actions',
    class: '',

    render: (member: IToAddMembersData, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <CollaboratorsToAddTableActions collaboratorData={member} onChange={onChange} />;
    }
  }
];

export const COLLABORATORS_WITH_ERROR_DOC_TYPE_COLUMN = {
  name: TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.docType,
  key: 'docType',
  isSortable: false,
  class: 'w-fit',
  render: (member: IMemberByFileData, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
    return <StyledTableEditableRowItem member={member} fieldName="docType" onChangeField={onChange} />;
  }
};

export const COLLABORATORS_WITH_ERROR_EXTRA_COLUM = [
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.salary,
    key: 'grossSalary',
    isSortable: false,
    class: 'w-fit',
    render: (member: IMemberByFileData, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={member} fieldName="grossSalary" onChangeField={onChange} />;
    }
  },
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.hoursWorked,
    key: 'workingHours',
    isSortable: false,
    class: 'w-fit',
    render: (member: IMemberByFileData, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem fieldName="workingHours" member={member} onChangeField={onChange} />;
    }
  },
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.zipCode,
    key: 'zipCode',
    isSortable: false,
    class: 'w-fit pr-18',
    render: (member: IMemberByFileData, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={member} fieldName="zipCode" onChangeField={onChange} />;
    }
  }
];

export const COLLABORATORS_WITH_ERROR_COLUMN = [
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.identification,
    key: 'docValue',
    isSortable: false,
    class: 'w-fit',
    render: (member: IMemberByFileData, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={member} fieldName="docValue" onChangeField={onChange} />;
    }
  },
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.fatherLastName,
    key: 'lastName',
    isSortable: false,
    class: 'w-fit',
    render: (member: IMemberByFileData, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={member} fieldName="lastName" onChangeField={onChange} />;
    }
  },
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.motherLastName,
    key: 'secondLastName',
    isSortable: false,
    class: 'w-fit',
    render: (member: IMemberByFileData, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={member} fieldName="secondLastName" onChangeField={onChange} />;
    }
  },
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.name,
    key: 'firstName',
    isSortable: false,
    class: 'w-fit',
    render: (member: IMemberByFileData, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={member} fieldName="firstName" onChangeField={onChange} />;
    }
  },
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.email,
    key: 'email',
    isSortable: false,
    class: 'w-fit',
    render: (member: IMemberByFileData, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={member} fieldName="email" onChangeField={onChange} />;
    }
  },
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.dateOfBrithday,
    key: 'birthdayDate',
    isSortable: false,
    class: 'w-fit',
    render: (member: IMemberByFileData, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={member} fieldName="birthdayDate" onChangeField={onChange} />;
    }
  }
];

export const COLLABORATORS_WITH_ERROR_GENDER_COLUMN = [
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.gender,
    key: 'gender',
    isSortable: false,
    class: 'w-fit',
    render: (member: IMemberByFileData, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={member} fieldName="gender" onChangeField={onChange} />;
    }
  }
];

export const getReportsTableColumns = (type: ReportTableType) => [
  {
    name: TRANSLATIONS_REPORTS.table.header.generationDate,
    key: 'generationDate',
    class: '',
    isSortable: true,
    render: (report: IReport) => {
      const isClosed = report.status.toLowerCase() === 'closed';
      return (
        <div className={`w-fit ${isClosed ? '' : 'disabled'}`}>
          {isClosed ? (
            <FormattedDate dateString={report.generationDate} />
          ) : (
            translate(TRANSLATIONS_REPORTS.table.reportStatus.pending)
          )}
        </div>
      );
    }
  },
  {
    name: type === 'monthly' ? TRANSLATIONS_REPORTS.table.header.period : TRANSLATIONS_REPORTS.table.header.cycle,
    key: 'period',
    class: '',
    isSortable: true,
    render: (report: IReport) => {
      const splitPeriod = report.period.split('-');
      return (
        <div className="w-fit">{capitalize(`${getLocalizedMonthName(Number(splitPeriod[1]))} ${splitPeriod[0]}`)}</div>
      );
    }
  },
  {
    name: TRANSLATIONS_REPORTS.table.header.status,
    key: 'status',
    class: '',
    isSortable: true,
    render: (report: IReport) => {
      const reportStatus = report.status.toLowerCase() === 'closed' ? 'generated' : 'toGenerate';
      const statusText = translate(TRANSLATIONS_REPORTS.table.reportStatus[reportStatus]);
      return (
        <BdsLabelPill
          text={(statusText || '').toUpperCase()}
          variant={REPORTS_STATUS[reportStatus].tagVariant}
          iconLeft={REPORTS_STATUS[reportStatus].icon}
        />
      );
    }
  },
  {
    name: '',
    key: 'actions',
    class: 'styled-table-hide-tablet table-align-right',
    render: (report: IReport) => {
      const disabled = report.status.toLowerCase() === 'open';
      return type === 'monthly' ? (
        <MonthlyReportsTableActions period={report.period} disabled={disabled} />
      ) : (
        <PayrollReportsTableActions id={report.id} disabled={disabled} />
      );
    }
  }
];

export const REPORTS_MONTHLY_TABLE_COLUMNS = getReportsTableColumns('monthly');
export const REPORTS_PAYROLL_TABLE_COLUMNS = getReportsTableColumns('payroll');

export const HEALTH_REPORT_TABLE_COLUMNS = [
  {
    name: TRANSLATIONS_REPORTS.table.header.generationDate,
    key: 'generationDate',
    class: '',
    isSortable: true,
    render: (report: IHealthReportList) => {
      return (
        <div className={'w-fit'}>
          <FormattedDate dateString={report.generationDate} />
        </div>
      );
    }
  },
  {
    name: TRANSLATIONS_REPORTS.table.header.period,
    key: 'period',
    class: '',
    isSortable: true,
    render: (report: IHealthReportList) => {
      const endMonth = report.endDate.split('-');
      const startMonth = report.dateStartHealth.split('-');
      const startDate = capitalize(`${getLocalizedMonthName(Number(startMonth[1]))} ${startMonth[0]}`);
      const endDate = capitalize(`${getLocalizedMonthName(Number(endMonth[1]))} ${endMonth[0]}`);
      return <div className="w-fit">{`${startDate} - ${endDate}`}</div>;
    }
  },
  {
    name: TRANSLATIONS_REPORTS.table.header.status,
    key: 'status',
    class: '',
    isSortable: true,
    render: (report: IHealthReportList) => {
      const reportStatus = report.status.toLowerCase() === 'closed' ? 'generated' : 'toGenerate';
      const statusText = translate(TRANSLATIONS_REPORTS.table.reportStatus[reportStatus]);
      return (
        <BdsLabelPill
          text={(statusText || '').toUpperCase()}
          variant={REPORTS_STATUS[reportStatus].tagVariant}
          iconLeft={REPORTS_STATUS[reportStatus].icon}
        />
      );
    }
  },
  {
    name: '',
    key: 'actions',
    class: 'styled-table-hide-tablet table-align-right',
    render: (report: IHealthReportList) => {
      const disabled = report.status.toLowerCase() === 'open';
      return <HealthReportsTableActions report={report} disabled={disabled} />;
    }
  }
];

export const COLLABORATOR_DEPENDENTS_TABLE_COLUMNS = [
  {
    name: TRANSLATION_COLLABORATORS.detail.healthProtection.dependentsCard.table.linkedCoverage,
    key: 'title',
    class: 'styled-table-body-small',
    render: (benefit: ICollaboratorDependantBenefit) => {
      return <div style={{ minWidth: 237 }}>{benefit.title}</div>;
    }
  },
  {
    name: TRANSLATION_COLLABORATORS.detail.healthProtection.dependentsCard.table.status,
    key: 'status',
    class: '',
    render: (benefit: ICollaboratorDependantBenefit) => {
      return (
        <div style={{ minWidth: 220 }}>
          {objectHasKey(benefit.status, BENEFIT_PROVIDER_STATUS_DATA) ? (
            <BdsLabelPill
              text={(translate(benefit.statusKey) || '').toUpperCase()}
              variant={BENEFIT_PROVIDER_STATUS_DATA[benefit.status].tagVariant as BdsLabelPillVariant}
              iconLeft={BENEFIT_PROVIDER_STATUS_DATA[benefit.status].icon as IBdsLabelPillImage}
              bold={false}
            />
          ) : (
            <div />
          )}
        </div>
      );
    }
  },
  {
    name: TRANSLATION_COLLABORATORS.detail.healthProtection.dependentsCard.table.validityStart,
    key: 'startDate',
    class: '',
    render: (benefit: ICollaboratorDependantBenefit) => {
      const isActive = benefit.status === 'active';
      return benefit.startDate && isActive ? (
        <div style={{ minWidth: 139 }}>{invertDateFormat(benefit.startDate)}</div>
      ) : (
        <div className="placeholder" style={{ minWidth: 139 }}>
          <BdsTooltip
            message={translate(TRANSLATION_COLLABORATORS.detail.healthProtection.dependentsCard.table.pendingTooltip)}
            position="top-center"
          >
            <span>{translate(TRANSLATION_COLLABORATORS.detail.healthProtection.dependentsCard.table.pending)}</span>
          </BdsTooltip>
        </div>
      );
    }
  },
  {
    name: TRANSLATION_COLLABORATORS.detail.healthProtection.dependentsCard.table.insuranceCompany,
    key: 'providerName',
    class: '',
    render: (benefit: ICollaboratorDependantBenefit) => {
      return <div style={{ minWidth: 200 }}>{benefit.providerName}</div>;
    }
  }
];

export const ADD_DEPENDENTS_ERROR_TABLE_COLUMNS = [
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.memberId,
    key: 'memberDocValue',
    class: 'w-fit pr-6',
    render: (dependent: IDependentsWithErrors, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={dependent} fieldName="memberDocValue" onChangeField={onChange} />;
    }
  },
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.docType,
    key: 'dependentDocType',
    class: 'w-fit pr-6',
    render: (dependent: IDependentsWithErrors, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={dependent} fieldName="dependentDocType" onChangeField={onChange} />;
    }
  },
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.dependentId,
    key: 'dependentDocValue',
    class: 'w-fit pr-6',
    render: (dependent: IDependentsWithErrors, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={dependent} fieldName="dependentDocValue" onChangeField={onChange} />;
    }
  },

  {
    name: TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.firstLastName,
    key: 'lastName',
    class: 'w-fit pr-6',
    render: (dependent: IDependentsWithErrors, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={dependent} fieldName="lastName" onChangeField={onChange} />;
    }
  },
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.secondLastName,
    key: 'secondLastName',
    class: 'w-fit pr-6',
    render: (dependent: IDependentsWithErrors, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return dependent.secondLastName?.value ? (
        <StyledTableEditableRowItem member={dependent} fieldName="secondLastName" onChangeField={onChange} />
      ) : (
        <div className="no-data" style={{ minWidth: 139 }}>
          {translate(TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.notInformed)}
        </div>
      );
    }
  },
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.name,
    key: 'firstName',
    class: 'w-fit pr-6',
    render: (dependent: IDependentsWithErrors, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={dependent} fieldName="firstName" onChangeField={onChange} />;
    }
  },
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.relationship,
    key: 'relationship',
    class: 'w-fit',
    render: (dependent: IDependentsWithErrors, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={dependent} fieldName="relationship" onChangeField={onChange} />;
    }
  },
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.birthDate,
    key: 'birthdayDate',
    class: 'w-fit pr-6',
    render: (dependent: IDependentsWithErrors, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={dependent} fieldName="birthdayDate" onChangeField={onChange} />;
    }
  },
  {
    name: TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.gender,
    key: 'gender',
    class: 'w-fit pr-32',
    render: (dependent: IDependentsWithErrors, _: boolean, __: GenericFunction, onChange: GenericFunction) => {
      return <StyledTableEditableRowItem member={dependent} fieldName="gender" onChangeField={onChange} />;
    }
  }
];

export const FLEXIBLE_REMUNERATION_REQUEST_TABLE_COLUMNS = [
  {
    name: TRANSLATIONS_REQUESTS.table.header.member,
    key: 'name',
    class: '',
    isSortable: true,
    render: (request: IFlexibleRemunerationRequest) => {
      const fullName = `${request.member.firstName} ${request.member.lastName}`;
      return (
        <div>
          <TruncatedText text={fullName} />
        </div>
      );
    }
  },
  {
    name: TRANSLATIONS_REQUESTS.table.header.type,
    key: 'category',
    isSortable: true,
    class: 'styled-table-hide-mobile challenges-table-column-status',
    render: (request: IFlexibleRemunerationRequest) => {
      let categoryText = '';
      if (request.category == 'health') {
        categoryText = translate(TRANSLATIONS_REQUESTS.table.filter.health);
      } else if (request.category == 'training') {
        categoryText = translate(TRANSLATIONS_REQUESTS.table.filter.training);
      }
      return <div>{categoryText}</div>;
    }
  },
  {
    name: TRANSLATIONS_REQUESTS.table.header.amount,
    key: 'amount',
    isSortable: true,
    class: 'styled-table-hide-mobile',
    render: (request: IFlexibleRemunerationRequest) => {
      return (
        <div>
          {toLocaleNumberFormat(
            request.amount,
            { style: 'currency', currency: request.currency, useGrouping: true, maximumFractionDigits: 2 },
            'es-ES'
          )}
        </div>
      );
    }
  },
  {
    name: TRANSLATIONS_REQUESTS.table.header.requestDate,
    key: 'createdAt',
    isSortable: true,
    class: 'styled-table-hide-tablet',
    render: (request: IFlexibleRemunerationRequest) => {
      return <div>{formatDateToDDMMYYYY(request.createdAt)}</div>;
    }
  },
  {
    name: TRANSLATIONS_REQUESTS.table.header.status,
    key: 'status',
    isSortable: true,
    class: 'styled-table-hide-tablet',
    render: (request: IFlexibleRemunerationRequest) => {
      return (
        <div style={{ minWidth: 220 }}>
          {objectHasKey(request.status, FLEXIBLE_REMUNERATION_REQUEST_STATUS) ? (
            <BdsLabelPill
              text={(translate(FLEXIBLE_REMUNERATION_REQUEST_STATUS[request.status].text) || '').toUpperCase()}
              variant={FLEXIBLE_REMUNERATION_REQUEST_STATUS[request.status].tagVariantTable as BdsLabelPillVariant}
              iconLeft={FLEXIBLE_REMUNERATION_REQUEST_STATUS[request.status].icon as IBdsLabelPillImage}
            />
          ) : (
            <div />
          )}
        </div>
      );
    }
  }
];
